import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster, toast } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { StorefrontConfigProvider } from "@/context/StorefrontConfigContext";
import { SEOProvider, useSEO } from "@/context/SEOContext";
import { HostProvider, useHost } from "@/context/HostContext";
import TenantNotFound, { TenantLoading } from "@/pages/TenantNotFound";
import SEOHead from "@/components/SEOHead";
import SEOApplier from "@/components/SEOApplier";
import NoIndexMeta from "@/components/NoIndexMeta";
import { orgJsonLd, websiteJsonLd, localBusinessJsonLd } from "@/lib/structuredData";
import { killServiceWorkersAndCaches } from "@/lib/swKiller";
import { initVersionCheck } from "@/lib/versionCheck";

import Layout from "@/components/Layout";
import HomePage from "@/pages/Home";
import CatalogPage from "@/pages/Catalog";
import ProductDetailPage from "@/pages/ProductDetail";
import CartPage from "@/pages/Cart";
import CheckoutPage from "@/pages/Checkout";
import WishlistPage from "@/pages/Wishlist";
import ProfilePage from "@/pages/Profile";
import { OrdersListPage, OrderDetailPage } from "@/pages/Orders";
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from "@/pages/Auth";

import AdminDashboard from "@/pages/admin/Dashboard";
import AdminSubscription from "@/pages/admin/AdminSubscription";
import AdminAccountSecurity from "@/pages/admin/AccountSecurity";
import AdminProducts from "@/pages/admin/Products";
import AdminCategories from "@/pages/admin/Categories";
import AdminShippingSettings from "@/pages/admin/ShippingSettings";
import { AdminOrders, AdminOrderDetail } from "@/pages/admin/Orders";
import AdminOrderCancellations from "@/pages/admin/OrderCancellations";
import AdminOrderRefunds from "@/pages/admin/OrderRefunds";
import AdminNotifications from "@/pages/admin/Notifications";
import AdminCustomers from "@/pages/admin/Customers";
import AdminPromos from "@/pages/admin/Promos";
import AdminBanners from "@/pages/admin/Banners";
import AdminPopups from "@/pages/admin/Popups";
import AdminSettings from "@/pages/admin/Settings";
import AdminBrandingSettings from "@/pages/admin/BrandingSettings";
import AdminSEOSettings from "@/pages/admin/SEOSettings";
import AdminCompanyProfile from "@/pages/admin/CompanyProfile";
import CompanyHomePage from "@/pages/CompanyHome";
import { CompanyProfileProvider, useCompanyProfile } from "@/context/CompanyProfileContext";
import AdminReviews from "@/pages/admin/Reviews";
import AdminReports from "@/pages/admin/Reports";
import AdminTeamAccess from "@/pages/admin/TeamAccess";
import AcceptInvitationPage from "@/pages/AcceptInvitation";
import AdminJubelio from "@/pages/admin/JubelioSettings";
import AdminBiteship from "@/pages/admin/BiteshipSettings";
import AdminTutorialCenter from "@/pages/admin/TutorialCenter";
import AdminJubelioTutorialGuide from "@/pages/admin/JubelioTutorialGuide";
import AdminRouteGuard from "@/components/AdminRouteGuard";
// Platform Owner (SaaS)
import { PlatformAuthProvider } from "@/context/PlatformAuthContext";
import { PlatformBrandingProvider } from "@/context/PlatformBrandingContext";
import PlatformLogin from "@/pages/platform/PlatformLogin";
import PlatformDashboard from "@/pages/platform/PlatformDashboard";
import PlatformTenants from "@/pages/platform/PlatformTenants";
import PlatformTenantDetail from "@/pages/platform/PlatformTenantDetail";
import PlatformSettingsPage from "@/pages/platform/PlatformSettings";
import LandingPageSettingsPage from "@/pages/platform/LandingPageSettings";
import PlatformFeatures from "@/pages/platform/PlatformFeatures";
import PlatformTemplates from "@/pages/platform/PlatformTemplates";
import PlatformSubscriptions from "@/pages/platform/PlatformSubscriptions";
import PlatformDeployments from "@/pages/platform/PlatformDeployments";
import DeploymentDetail from "@/pages/platform/deployments/DeploymentDetail";
import PlatformEnvironments from "@/pages/platform/PlatformEnvironments";
import EnvironmentDetail from "@/pages/platform/environments/EnvironmentDetail";
import PlatformConfigurations from "@/pages/platform/PlatformConfigurations";
import ConfigurationDetail from "@/pages/platform/configurations/ConfigurationDetail";
import PlatformProvisioning from "@/pages/platform/PlatformProvisioning";
import ProvisioningDetail from "@/pages/platform/provisioning/ProvisioningDetail";
import PlatformDeploymentRuns from "@/pages/platform/PlatformDeploymentRuns";
import DeploymentRunDetail from "@/pages/platform/deployments/DeploymentRunDetail";
import PlatformInfrastructure from "@/pages/platform/PlatformInfrastructure";
import InfrastructureDetail from "@/pages/platform/infrastructure/InfrastructureDetail";
import PlatformWebsites from "@/pages/platform/PlatformWebsites";
import WebsiteDetail from "@/pages/platform/websites/WebsiteDetail";
import PlatformMonitoring from "@/pages/platform/PlatformMonitoring";
import MonitoringDetail from "@/pages/platform/monitoring/MonitoringDetail";
import PlatformBilling from "@/pages/platform/PlatformBilling";
import BillingDetail from "@/pages/platform/billing/BillingDetail";
import PlatformLicenses from "@/pages/platform/PlatformLicenses";
import LicenseDetail from "@/pages/platform/licenses/LicenseDetail";
import PlatformScheduler from "@/pages/platform/PlatformScheduler";
import PlatformDomains from "@/pages/platform/PlatformDomains";
import DomainDetail from "@/pages/platform/domains/DomainDetail";
import PlatformDomainBinding from "@/pages/platform/PlatformDomainBinding";
import PlatformGoLive from "@/pages/platform/PlatformGoLive";
import LaunchCenter from "@/pages/platform/LaunchCenter";
import PlatformTemplateRegistry from "@/pages/platform/PlatformTemplateRegistry";
import TemplateDesigner from "@/pages/platform/templates/TemplateDesigner";
import CreateTenantFromTemplateWizard from "@/pages/platform/CreateTenantFromTemplateWizard";
import PlatformTeam from "@/pages/platform/PlatformTeam";
import PlatformAccount from "@/pages/platform/PlatformAccount";

// Feature Flags — hook + provider
import { FeatureFlagProvider } from "@/hooks/useFeatureFlag";
import { TemplateProvider } from "@/hooks/useTemplate";
import { SubscriptionProvider } from "@/hooks/useSubscription";
import { TutorialProvider } from "@/context/TutorialContext";

/* Dual-mode Phase 1: `/` (dan catch-all) memilih Company Profile vs Store Homepage.
   Default & fail-safe = Store (perilaku existing). /katalog, /produk, cart, checkout tidak berubah. */
function StorefrontHomeEntry() {
  const { mode, loading } = useCompanyProfile();
  if (loading) return null;
  return mode === "company_profile" ? <CompanyHomePage /> : <HomePage />;
}

function WithLayout({ children, seoTitle, seoDescription, seoRobots, seoCanonical }) {
  return (
    <Layout>
      <SEOHead title={seoTitle} description={seoDescription} robots={seoRobots} canonical={seoCanonical} />
      {children}
    </Layout>
  );
}

/**
 * GlobalStructuredData — imperatively inject site-wide Organization, WebSite
 * and LocalBusiness JSON-LD into <head> whenever branding changes.
 * Kept separate from page-level SEOHead so both can coexist without conflict.
 */
function GlobalStructuredData() {
  const { seo } = useSEO();
  const host = useHost();
  // Phase D: JSON-LD Organization/WebSite/LocalBusiness milik tenant hanya pada host tenant —
  // host platform/preview/unknown tidak boleh mempublikasikan identitas tenant default.
  const isTenantHost = host?.kind === "tenant";
  useEffect(() => {
    document.querySelectorAll('script[type="application/ld+json"][data-managed="seohead-global"]').forEach((s) => s.remove());
    if (!isTenantHost) return;
    const branding = seo._branding;
    const lds = [
      orgJsonLd(branding),
      websiteJsonLd(branding),
      localBusinessJsonLd(branding, seo.local_business),
    ].filter(Boolean);
    lds.forEach((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-managed", "seohead-global");
      s.textContent = JSON.stringify(obj);
      document.head.appendChild(s);
    });
  }, [seo, isTenantHost]);
  return null;
}

function App() {
  const [, setUpdateAvailable] = useState(false);

  useEffect(() => {
    // Defensive cleanup: kalau deploy sebelumnya pernah register SW, kill it
    // beserta seluruh cache supaya user TIDAK terjebak bundle lama.
    killServiceWorkersAndCaches();
    // Poll /version.txt — tampilkan banner kalau ada deploy baru.
    const cleanup = initVersionCheck(({ current, next }) => {
      console.info(`[versionCheck] new build detected: ${current} → ${next}`);
      setUpdateAvailable(true);
      toast.info("Versi baru tersedia. Muat ulang untuk menggunakan versi terbaru.", {
        id: "app-update-available",
        duration: Infinity,
        action: {
          label: "Muat Ulang",
          onClick: () => {
            try {
              sessionStorage.removeItem("__sw_killed");
            } catch (_) { /* ignore */ }
            window.location.reload();
          },
        },
      });
    });
    return cleanup;
  }, []);

  return (
    <HostProvider>
    <AuthProvider>
      <BrandingProvider>
        <StorefrontConfigProvider>
        <SEOProvider>
        <CartProvider>
          <FeatureFlagProvider>
          <TemplateProvider>
          <SubscriptionProvider>
          <PlatformAuthProvider>
          <PlatformBrandingProvider>
          <TutorialProvider>
          <BrowserRouter>
            <Toaster richColors position="top-center" />
            <GlobalStructuredData />
            <SEOApplier />
            <HostAwareRouter />
          </BrowserRouter>
          </TutorialProvider>
          </PlatformBrandingProvider>
          </PlatformAuthProvider>
          </SubscriptionProvider>
          </TemplateProvider>
          </FeatureFlagProvider>
        </CartProvider>
        </SEOProvider>
        </StorefrontConfigProvider>
      </BrandingProvider>
    </AuthProvider>
    </HostProvider>
  );
}

// ==================== HOST-AWARE ROUTER ====================
function HostAwareRouter() {
  const { kind, host, tenant, loading, error } = useHost();

  // Tenant host: gate platform routes + show storefront/admin only.
  if (kind === "tenant") {
    if (loading) return <TenantLoading host={host} />;
    if (error || !tenant) return <TenantNotFound host={host} reason={error || "not-found"} />;
    return <CompanyProfileProvider><TenantRoutes /></CompanyProfileProvider>;
  }
  // Unknown host (bare apex, unrelated domain) — treat as not-found.
  if (kind === "unknown") {
    return <><NoIndexMeta /><TenantNotFound host={host} reason="not-found" /></>;
  }
  // Default: platform host (app.dynicty.com / preview / localhost) → Owner Portal only.
  // Storefront/tenant-admin routes are NOT registered here to prevent tenant
  // storefront from ever bleeding into the platform host.
  return <><NoIndexMeta /><PlatformRoutes /></>;
}

// Storefront + tenant admin routes only. Platform paths are redirected to root
// so that visitors typing /platform on a tenant host don't see the Owner Portal.
function TenantRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Customer storefront pages */}
      <Route path="/" element={<WithLayout><StorefrontHomeEntry /></WithLayout>} />
      <Route path="/katalog" element={<WithLayout seoTitle="Katalog Produk"><CatalogPage /></WithLayout>} />
      <Route path="/produk/:slug" element={<WithLayout><ProductDetailPage /></WithLayout>} />
      <Route path="/cart" element={<WithLayout seoTitle="Keranjang Belanja"><CartPage /></WithLayout>} />
      <Route path="/checkout" element={<WithLayout seoTitle="Checkout"><CheckoutPage /></WithLayout>} />
      <Route path="/wishlist" element={<WithLayout seoTitle="Wishlist"><WishlistPage /></WithLayout>} />
      <Route path="/profile" element={<WithLayout seoTitle="Profil Saya"><ProfilePage /></WithLayout>} />
      <Route path="/pesanan" element={<WithLayout seoTitle="Pesanan Saya"><OrdersListPage /></WithLayout>} />
      <Route path="/pesanan/:orderId" element={<WithLayout seoTitle="Detail Pesanan"><OrderDetailPage /></WithLayout>} />

      {/* Tenant Admin pages (must remain accessible from tenant host) */}
      <Route path="/admin" element={<AdminRouteGuard featureKey="core.dashboard" permission="dashboard.view"><AdminDashboard /></AdminRouteGuard>} />
      <Route path="/admin/login" element={<LoginPage />} />
      <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
      <Route path="/admin/produk" element={<AdminRouteGuard featureKey="core.products" permission="products.view"><AdminProducts /></AdminRouteGuard>} />
      <Route path="/admin/kategori" element={<AdminRouteGuard featureKey="core.categories" permission="categories.manage"><AdminCategories /></AdminRouteGuard>} />
      <Route path="/admin/shipping" element={<AdminRouteGuard featureKey="shipping.settings" permission="shipping.manage"><AdminShippingSettings /></AdminRouteGuard>} />
      <Route path="/admin/pesanan" element={<AdminRouteGuard featureKey="core.orders" permission="orders.view"><AdminOrders /></AdminRouteGuard>} />
      <Route path="/admin/pesanan/pembatalan" element={<AdminRouteGuard featureKey="core.orders" permission="orders.view"><AdminOrderCancellations /></AdminRouteGuard>} />
      <Route path="/admin/pesanan/refund" element={<AdminRouteGuard featureKey="core.orders" permission="orders.view"><AdminOrderRefunds /></AdminRouteGuard>} />
      <Route path="/admin/pesanan/:orderId" element={<AdminRouteGuard featureKey="core.orders" permission="orders.view"><AdminOrderDetail /></AdminRouteGuard>} />
      <Route path="/admin/notifications" element={<AdminRouteGuard featureKey="notifications.in_app" permission="dashboard.view"><AdminNotifications /></AdminRouteGuard>} />
      <Route path="/admin/pelanggan" element={<AdminRouteGuard featureKey="core.customers" permission="customers.view"><AdminCustomers /></AdminRouteGuard>} />
      <Route path="/admin/promo" element={<AdminRouteGuard featureKey="marketing.voucher" permission="banners.manage"><AdminPromos /></AdminRouteGuard>} />
      <Route path="/admin/banner" element={<AdminRouteGuard featureKey="marketing.banner" permission="banners.manage"><AdminBanners /></AdminRouteGuard>} />
      <Route path="/admin/popup" element={<AdminRouteGuard featureKey="marketing.popup_banner" permission="banners.manage"><AdminPopups /></AdminRouteGuard>} />
      <Route path="/admin/pengaturan" element={<AdminRouteGuard featureKey="core.settings" permission="settings.manage"><AdminSettings /></AdminRouteGuard>} />
      <Route path="/admin/akun" element={<AdminRouteGuard><AdminAccountSecurity /></AdminRouteGuard>} />
      <Route path="/admin/tutorials" element={<AdminRouteGuard permission="dashboard.view"><AdminTutorialCenter /></AdminRouteGuard>} />
      <Route path="/admin/tutorials/jubelio" element={<AdminRouteGuard permission="dashboard.view"><AdminJubelioTutorialGuide /></AdminRouteGuard>} />
      <Route path="/admin/branding" element={<AdminRouteGuard featureKey="branding.platform" permission="branding.manage"><AdminBrandingSettings /></AdminRouteGuard>} />
      <Route path="/admin/seo" element={<AdminRouteGuard featureKey="seo.platform" permission="branding.manage"><AdminSEOSettings /></AdminRouteGuard>} />
      <Route path="/admin/company-profile" element={<AdminRouteGuard featureKey="storefront.company_profile" permission="branding.manage"><AdminCompanyProfile /></AdminRouteGuard>} />
      <Route path="/admin/ulasan" element={<AdminRouteGuard featureKey="reviews.product" permission="products.manage"><AdminReviews /></AdminRouteGuard>} />
      <Route path="/admin/laporan" element={<AdminRouteGuard featureKey="reports.basic" permission="reports.basic"><AdminReports /></AdminRouteGuard>} />
      <Route path="/admin/jubelio" element={<AdminRouteGuard featureKey="integration.jubelio" permission="shipping.manage"><AdminJubelio /></AdminRouteGuard>} />
      <Route path="/admin/biteship" element={<AdminRouteGuard featureKey="integration.biteship" permission="shipping.manage"><AdminBiteship /></AdminRouteGuard>} />
      <Route path="/admin/tim" element={<AdminRouteGuard featureKey="core.settings" permission="team.view"><AdminTeamAccess /></AdminRouteGuard>} />
      <Route path="/admin/langganan" element={<AdminRouteGuard featureKey="core.subscription" permission="subscription.manage"><AdminSubscription /></AdminRouteGuard>} />

      {/* Platform paths on tenant host → redirect to storefront root.
          This prevents Owner Portal from ever being reachable on a tenant subdomain. */}
      <Route path="/platform" element={<Navigate to="/" replace />} />
      <Route path="/platform/*" element={<Navigate to="/" replace />} />

      {/* Catch-all — storefront home for graceful fallback */}
      {/* SEO Phase C: catch-all = soft-404 (render Home) → noindex, canonical ke "/" */}
      <Route path="*" element={<WithLayout seoRobots="noindex,follow" seoCanonical="/"><StorefrontHomeEntry /></WithLayout>} />
    </Routes>
  );
}

// PlatformRoutes — HANYA untuk platform host (app.dynicty.com / preview / localhost).
// TIDAK boleh mendaftarkan route storefront tenant maupun /admin/* (tenant admin).
// Root "/" pada platform host redirect ke "/platform" (guard PlatformLayout akan
// forward ke "/platform/login" bila belum login).
function PlatformRoutes() {
  return (
    <Routes>
      {/* Root platform host → Owner Portal. */}
      <Route path="/" element={<Navigate to="/platform" replace />} />

      {/* Accept invitation harus reachable dari host manapun (token-only, host-agnostic) */}
      <Route path="/accept-invitation" element={<AcceptInvitationPage />} />

      {/* Platform Owner (SaaS Dashboard) */}
      <Route path="/platform/login" element={<PlatformLogin />} />
      <Route path="/platform" element={<PlatformDashboard />} />
      <Route path="/platform/tenants" element={<PlatformTenants />} />
      <Route path="/platform/tenants/create" element={<CreateTenantFromTemplateWizard />} />
      <Route path="/platform/tenants/:id" element={<PlatformTenantDetail />} />
      <Route path="/platform/team" element={<PlatformTeam />} />
      <Route path="/platform/account" element={<PlatformAccount />} />
      <Route path="/platform/settings" element={<PlatformSettingsPage />} />
      <Route path="/platform/settings/landing-page" element={<LandingPageSettingsPage />} />
      <Route path="/platform/features" element={<PlatformFeatures />} />
      <Route path="/platform/templates" element={<PlatformTemplates />} />
      <Route path="/platform/subscriptions" element={<PlatformSubscriptions />} />
      <Route path="/platform/deployments" element={<PlatformDeployments />} />
      <Route path="/platform/deployments/environments" element={<PlatformEnvironments />} />
      <Route path="/platform/deployments/environments/:id" element={<EnvironmentDetail />} />
      <Route path="/platform/deployments/runs" element={<PlatformDeploymentRuns />} />
      <Route path="/platform/deployments/runs/:id" element={<DeploymentRunDetail />} />
      <Route path="/platform/deployments/:id" element={<DeploymentDetail />} />
      <Route path="/platform/configurations" element={<PlatformConfigurations />} />
      <Route path="/platform/configurations/:id" element={<ConfigurationDetail />} />
      <Route path="/platform/provisioning" element={<PlatformProvisioning />} />
      <Route path="/platform/provisioning/:id" element={<ProvisioningDetail />} />
      <Route path="/platform/infrastructure" element={<PlatformInfrastructure />} />
      <Route path="/platform/infrastructure/:id" element={<InfrastructureDetail />} />
      <Route path="/platform/websites" element={<PlatformWebsites />} />
      <Route path="/platform/websites/:id" element={<WebsiteDetail />} />
      <Route path="/platform/monitoring" element={<PlatformMonitoring />} />
      <Route path="/platform/monitoring/:tenantId" element={<MonitoringDetail />} />
      <Route path="/platform/billing" element={<PlatformBilling />} />
      <Route path="/platform/billing/tenant/:tenantId" element={<BillingDetail />} />
      <Route path="/platform/licenses" element={<PlatformLicenses />} />
      <Route path="/platform/licenses/:tenantId" element={<LicenseDetail />} />
      <Route path="/platform/scheduler" element={<PlatformScheduler />} />
      <Route path="/platform/domains" element={<PlatformDomains />} />
      <Route path="/platform/domains/:id" element={<DomainDetail />} />
      <Route path="/platform/auto-bind" element={<PlatformDomainBinding />} />
      <Route path="/platform/go-live" element={<PlatformGoLive />} />
      <Route path="/platform/launch" element={<LaunchCenter />} />
      <Route path="/platform/template-registry" element={<PlatformTemplateRegistry />} />
      <Route path="/platform/template-registry/:id/designer" element={<TemplateDesigner />} />

      {/* Fallback: setiap path lain pada platform host diarahkan ke Owner Portal.
          Storefront routes DIHAPUS agar tidak bocor ke app.dynicty.com. */}
      <Route path="*" element={<Navigate to="/platform" replace />} />
    </Routes>
  );
}

export default App;
